import {ReactNode, useState} from "react"
import {
    CircularProgress,
    Container,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    TextField, Tooltip
} from "@mui/material"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import {useTranslation} from "react-i18next";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';


interface IProps {
    formLabels: string[]
    formData: any
    submitText?: string
    onChange: (key: string, value: any) => void
    onSubmit?: (formData: any) => void
    formHeader?: string
    editable?: boolean
    children?: ReactNode
    loading?: boolean
}

const TextForm = ({
                      formLabels,
                      formData,
                      submitText,
                      onChange,
                      onSubmit,
                      formHeader,
                      editable,
                      children,
                      loading
                  }: IProps) => {

    const {t} = useTranslation()
    const [errors, setErrors] = useState<any>({})
    const [showPassword, setShowPassword] = useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const renderForm = (formLabels: string[], formData: any, onChange: (key: any, value: any) => void) => {
        return Object.keys(formData).map((key: string, index: number) => {
            const {value, required, multiline, hidden, date, percentage} = formData[key];
            const label = formLabels[index]
            const isPasswordField = key.toLowerCase().includes('password')
            const isEmailField = key.toLowerCase().includes('email')
            const isNumberField = typeof value === "number"

            return (
                (hidden === undefined || hidden === false) &&
                <FormControl
                    sx={{
                        width: '100%',
                    }}
                    key={key}
                    error={!!errors[key]}
                >
                    <TextField
                        fullWidth
                        contentEditable={editable}
                        margin="normal"
                        multiline={!!multiline}
                        rows={4}
                        label={label}
                        value={value}
                        required={required}
                        onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
                        onChange={(e) => {
                            const newValue = isNumberField ? Number(e.target.value) : e.target.value
                            onChange(key, newValue)
                        }}
                        type={isPasswordField && !showPassword ? 'password' : isEmailField ? 'email' : isNumberField ? 'number' : date ? 'date' : 'text'}
                        slotProps={{
                            input: {
                                endAdornment:
                                    isPasswordField ?
                                        <InputAdornment position="end">
                                            <Tooltip title={t("SHOW_PASSWORD")}><IconButton
                                                aria-label={
                                                    showPassword ? 'hide the password' : 'display the password'
                                                }
                                                size="small"
                                                onClick={handleClickShowPassword}
                                            >
                                                {showPassword ? <VisibilityOff/> : <Visibility/>}
                                            </IconButton></Tooltip>
                                        </InputAdornment> :
                                        percentage && <InputAdornment position="end">%</InputAdornment>,
                            }
                        }}
                    />
                    {errors[key] && <FormHelperText>{errors[key]}</FormHelperText>}
                </FormControl>
            );
        });
    };

    const isFormValid = () => {
        const newErrors: any = {};
        Object.keys(formData).forEach((key) => {
            if (!formData[key].value.trim() && formData[key].required) {
                newErrors[key] = `${t('FORM_FIELD_REQUIRED')}`
            }

            if (key.toLowerCase().includes('email') && formData[key].value && !/\S+@\S+\.\S+/.test(formData[key].value)) {
                newErrors[key] = `${t('FORM_EMAIL_INVALID')}`
            }
        });
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    };

    return (
        <Container>
            {formHeader && (
                <Typography sx={{textAlign: 'left'}} variant={'h5'}> {
                    formHeader}
                </Typography>
            )}
            {renderForm(formLabels, formData, onChange)}
            {onSubmit && (
                <Button
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={() => {
                        if (isFormValid())
                            onSubmit(formData)
                    }}
                    sx={{marginTop: '12px', marginBottom: '12px', width: '100%'}}
                >
                    {!loading ? <Typography>{submitText}</Typography> : <CircularProgress/>}
                </Button>
            )}

            {children}
        </Container>
    )
}

export default TextForm;